import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MdPeopleAlt,
  MdEdit,
  MdVisibility,
  MdSearch,
  MdRefresh,
  MdAdminPanelSettings,
  MdCampaign,
  MdMemory,
  MdPerson,
  MdAdd,
  MdLogout,
  MdDeleteOutline,
} from "react-icons/md";
import "../../styles/DashBoard_Profile.css";
import "../../styles/UserModal.css";
import ShowSnacker from "./ShowSnacker";
import AddUserModal from "./AddUserModal";
import ViewUserModal from "./ViewUserModal";
import EditUserModal from "./EditUserModal";
import { apiFetch } from "../../services/api";
import { getCurrentUserRole, hasPermission, normalizeRole } from "../../utils/rbac";

export interface UserItem {
  id?: number | string;
  adminId?: number | string;
  username: string;
  emailId: string;
  phonenumber?: string;
  mobileNumber?: string;
  role: string;
  createdAt?: string;
  updatedAt?: string;
}

function DashBoardProfile() {
  const navigate = useNavigate();
  const location = useLocation();

  const [userRole, setUserRole] = useState(getCurrentUserRole());
  const canManageUsers = hasPermission("canAccessUserManagement", userRole);
  const canDeleteUser = hasPermission("canDeleteUser", userRole);

  // If path is /dashboard/users and role can manage users, show user management table
  // Otherwise, show personal profile view
  const isUsersPath = location.pathname.includes("/users");
  const [activeTab, setActiveTab] = useState<"users" | "profile">(
    isUsersPath && canManageUsers ? "users" : "profile"
  );

  // Listen for role updates so UI updates immediately when role changes
  useEffect(() => {
    const handleRoleChange = (e: any) => {
      const fresh = e?.detail?.role ? normalizeRole(e.detail.role) : getCurrentUserRole();
      setUserRole(fresh);
    };
    window.addEventListener("roleChanged", handleRoleChange);
    return () => window.removeEventListener("roleChanged", handleRoleChange);
  }, []);

  useEffect(() => {
    if (canManageUsers && location.pathname.includes("/users")) {
      setActiveTab("users");
    } else {
      setActiveTab("profile");
    }
  }, [location.pathname, canManageUsers]);

  const showUserManagement = canManageUsers && activeTab === "users";

  const [currentUserProfile, setCurrentUserProfile] = useState<UserItem | null>(null);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<string>("ALL");

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 8;

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserItem | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<UserItem | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Snackbar state
  const [snack, setSnack] = useState({ open: false, message: "", type: "info" });
  const showSnackbar = (message: string, type = "info") => {
    setSnack({ open: true, message, type });
  };

  // Fetch data: always fetches current profile; fetches all users ONLY if canManageUsers
  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Fetch current profile from /admin/profile
      const profileRes = await apiFetch("/admin/profile", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        const currentProfile = Array.isArray(profileData)
          ? profileData[0]
          : profileData;
        if (currentProfile && (currentProfile.emailId || currentProfile.username)) {
          const freshRole = normalizeRole(currentProfile.role || userRole);
          setCurrentUserProfile({
            ...currentProfile,
            role: freshRole,
          });
          if (freshRole !== userRole) {
            setUserRole(freshRole);
            localStorage.setItem("userRole", freshRole);
            window.dispatchEvent(
              new CustomEvent("roleChanged", { detail: { role: freshRole } })
            );
          }
        }
      } else {
        setCurrentUserProfile({
          username: localStorage.getItem("userName") || "User",
          emailId: localStorage.getItem("userEmail") || "user@studds.com",
          role: userRole,
          phonenumber: "+91 9876543210",
          createdAt: new Date().toISOString(),
        });
      }

      // 2. Fetch all users if Administrator
      if (canManageUsers) {
        const res = await apiFetch("/admin/all", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (res.ok) {
          const data = await res.json();
          const userList: UserItem[] = Array.isArray(data)
            ? data
            : data.data || data.users || [];
          if (userList.length > 0) {
            setUsers(userList);
            return;
          }
        }

        // Fallback cached / default admin if /admin/all returned empty
        const cached = localStorage.getItem("studds_custom_users");
        const customList: UserItem[] = cached ? JSON.parse(cached) : [];
        const fallbackAdmin: UserItem = {
          id: 1,
          username: localStorage.getItem("userName") || "It.alert@studds.com",
          emailId: localStorage.getItem("userEmail") || "It.alert@studds.com",
          phonenumber: "+91 9876543210",
          role: "Administrator",
          createdAt: new Date().toISOString(),
        };
        setUsers([fallbackAdmin, ...customList]);
      }
    } catch (err) {
      console.error("Error loading profile/user data:", err);
      showSnackbar("Failed to load user data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [canManageUsers]);

  // Filter users based on search and role (for Administrator User Management)
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const name = (u.username || "").toLowerCase();
      const email = (u.emailId || "").toLowerCase();
      const phone = (u.phonenumber || u.mobileNumber || "").toLowerCase();
      const role = (u.role || "").toLowerCase();
      const query = searchQuery.toLowerCase().trim();

      const matchesSearch =
        !query ||
        name.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        role.includes(query);

      const matchesRole =
        selectedRole === "ALL" ||
        role === selectedRole.toLowerCase();

      return matchesSearch && matchesRole;
    });
  }, [users, searchQuery, selectedRole]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage) || 1;
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(start, start + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  // Statistics
  const stats = useMemo(() => {
    const total = users.length;
    const superAdminCount = users.filter((u) =>
      ["super admin", "superadmin"].includes((u.role || "").toLowerCase())
    ).length;
    const adminCount = users.filter((u) =>
      ["admin", "administrator"].includes((u.role || "").toLowerCase())
    ).length;
    const marketingCount = users.filter((u) =>
      (u.role || "").toLowerCase().includes("marketing")
    ).length;
    const firmwareCount = users.filter((u) =>
      (u.role || "").toLowerCase().includes("firmware")
    ).length;
    return { total, superAdminCount, adminCount, marketingCount, firmwareCount };
  }, [users]);

  // Actions
  const handleOpenView = (user: UserItem) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const handleOpenEdit = (user: UserItem | null) => {
    if (!user) return;
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleEditUserSuccess = (msg: string, updatedUser: UserItem) => {
    showSnackbar(msg || "User updated successfully!", "success");
    if (updatedUser) {
      const myEmail = (localStorage.getItem("userEmail") || "").toLowerCase();
      if (updatedUser.emailId && updatedUser.emailId.toLowerCase() === myEmail) {
        const normalized = normalizeRole(updatedUser.role);
        localStorage.setItem("userRole", normalized);
        setUserRole(normalized);
        window.dispatchEvent(
          new CustomEvent("roleChanged", { detail: { role: normalized } })
        );
      }

      if (
        currentUserProfile &&
        (currentUserProfile.emailId === updatedUser.emailId ||
          currentUserProfile.id === updatedUser.id ||
          currentUserProfile.adminId === updatedUser.adminId)
      ) {
        setCurrentUserProfile((prev) => (prev ? { ...prev, ...updatedUser } : updatedUser));
        if (updatedUser.username) {
          localStorage.setItem("userName", updatedUser.username);
        }
      }

      setUsers((prev) =>
        prev.map((u) =>
          u.emailId === updatedUser.emailId ||
          (u.id && u.id === updatedUser.id) ||
          (u.adminId && u.adminId === updatedUser.adminId)
            ? { ...u, ...updatedUser }
            : u
        )
      );
    } else {
      loadData();
    }
  };

  const handleAddUserSuccess = (msg: string, newUser?: UserItem) => {
    showSnackbar(msg || "User added successfully!", "success");
    if (newUser) {
      setUsers((prev) => [newUser, ...prev]);
    } else {
      loadData();
    }
  };

  const handleOpenDelete = (user: UserItem) => {
    if (!user) return;
    const myEmail = (
      localStorage.getItem("userEmail") ||
      currentUserProfile?.emailId ||
      ""
    ).toLowerCase().trim();
    const targetEmail = (user.emailId || "").toLowerCase().trim();

    if (myEmail && targetEmail && myEmail === targetEmail) {
      showSnackbar("You cannot delete your own account.", "warning");
      return;
    }

    const protectedAccounts = [
      "admin@studds.com",
      "admin@gmail.com",
      "superadmin@studds.com",
      "super admin",
      "it.alert@studds.com",
    ];
    if (protectedAccounts.includes(targetEmail) || protectedAccounts.includes((user.username || "").toLowerCase())) {
      showSnackbar("Protected root administrator / Super Admin account cannot be deleted.", "warning");
      return;
    }

    setDeleteCandidate(user);
  };

  const confirmDeleteUser = async () => {
    if (!deleteCandidate) return;
    setIsDeleting(true);
    try {
      const identifier =
        deleteCandidate.emailId || deleteCandidate.username || deleteCandidate.id;
      const res = await apiFetch(`/admin/${encodeURIComponent(String(identifier))}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        showSnackbar(
          data.message || `User ${deleteCandidate.username || deleteCandidate.emailId} deleted successfully!`,
          "success"
        );

        // Remove deleted user from state immediately
        setUsers((prev) =>
          prev.filter((u) => {
            if (deleteCandidate.emailId && u.emailId) {
              return u.emailId.toLowerCase() !== deleteCandidate.emailId.toLowerCase();
            }
            if (deleteCandidate.id && u.id) {
              return u.id !== deleteCandidate.id;
            }
            return u.username !== deleteCandidate.username;
          })
        );

        // Remove from local custom cache if present
        try {
          const cached = localStorage.getItem("studds_custom_users");
          if (cached) {
            const list: UserItem[] = JSON.parse(cached);
            const filtered = list.filter(
              (u) =>
                u.emailId?.toLowerCase() !== deleteCandidate.emailId?.toLowerCase() &&
                u.username !== deleteCandidate.username
            );
            localStorage.setItem("studds_custom_users", JSON.stringify(filtered));
          }
        } catch (e) {
          // ignore
        }

        setDeleteCandidate(null);
      } else {
        showSnackbar(data.message || "Failed to delete user", "error");
      }
    } catch (err: any) {
      console.error("Delete user error:", err);
      showSnackbar(err.message || "An error occurred while deleting user", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedRole("ALL");
    setCurrentPage(1);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("auth");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  return (
    <div className="dashboard-profile user-list-container">
      {/* Top Header */}
      <div className="user-list-header">
        <div className="header-title-section">
          <h2>{showUserManagement ? "User Management" : "My Profile"}</h2>
          <p>
            {showUserManagement
              ? "View, search, edit, and add users across your organization"
              : "View and update your personal account information"}
          </p>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {showUserManagement && canManageUsers && (
            <button
              type="button"
              className="add-user-btn"
              onClick={() => setIsAddModalOpen(true)}
            >
              <MdAdd size={18} />
              <span>Add New User</span>
            </button>
          )}

          {!showUserManagement && currentUserProfile && (
            <button
              type="button"
              className="add-user-btn"
              onClick={() => handleOpenEdit(currentUserProfile)}
            >
              <MdEdit size={18} />
              <span>Edit Profile</span>
            </button>
          )}

          {canManageUsers && (
            <button
              type="button"
              className="user-btn-secondary"
              onClick={() => {
                if (showUserManagement) {
                  setActiveTab("profile");
                  navigate("/dashboard/profile");
                } else {
                  setActiveTab("users");
                  navigate("/dashboard/users");
                }
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                height: "38px",
                padding: "0 14px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                background: "#fff",
                fontWeight: 500,
                fontSize: "13px",
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              {showUserManagement ? <MdPerson size={18} /> : <MdPeopleAlt size={18} />}
              <span>{showUserManagement ? "My Profile" : "Manage Users"}</span>
            </button>
          )}

          <div className="icons" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <span
              className="tooltip"
              data-tooltip="Refresh"
              style={{
                cursor: "pointer",
                width: "38px",
                height: "38px",
                borderRadius: "50%",
                background: "#f3f4f6",
                border: "1px solid #e5e7eb",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onClick={loadData}
              title="Refresh"
            >
              <MdRefresh size={20} color="#4b5563" />
            </span>
            <span
              className="tooltip"
              data-tooltip="Logout"
              style={{
                cursor: "pointer",
                width: "38px",
                height: "38px",
                borderRadius: "50%",
                background: "#fee2e2",
                border: "1px solid #fecaca",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onClick={handleLogout}
              title="Logout"
            >
              <MdLogout size={20} color="#dc2626" />
            </span>
          </div>
        </div>
      </div>

      {/* ===================== MY PROFILE VIEW ===================== */}
      {!showUserManagement && (
        <div
          className="my-profile-container"
          style={{
            maxWidth: "960px",
            margin: "20px 0",
            background: "#ffffff",
            borderRadius: "14px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            overflow: "hidden",
          }}
        >
          {/* Header Banner */}
          <div
            style={{
              background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
              padding: "32px",
              display: "flex",
              alignItems: "center",
              gap: "24px",
              color: "#fff",
            }}
          >
            <div
              style={{
                width: "76px",
                height: "76px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #e60000 0%, #b91c1c 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "30px",
                fontWeight: "700",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(230, 0, 0, 0.4)",
                border: "3px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              {(currentUserProfile?.username || "U")[0]?.toUpperCase() || "U"}
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "700", color: "#ffffff" }}>
                  {currentUserProfile?.username || "User"}
                </h2>
                <span
                  className={`role-badge ${(currentUserProfile?.role || userRole)
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  {currentUserProfile?.role || userRole}
                </span>
              </div>
              <p
                style={{
                  margin: "6px 0 0 0",
                  color: "#9ca3af",
                  fontSize: "14px",
                }}
              >
                {currentUserProfile?.emailId || "user@studds.com"}
              </p>
            </div>
          </div>

          {/* Profile Details Grid */}
          <div style={{ padding: "30px" }}>
            <h3
              style={{
                margin: "0 0 18px 0",
                fontSize: "15px",
                fontWeight: 600,
                color: "#374151",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Account Details
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "20px 24px",
                background: "#f9fafb",
                padding: "24px",
                borderRadius: "10px",
                border: "1px solid #f3f4f6",
              }}
            >
              <div className="view-detail-item">
                <span className="view-detail-label">Full Name</span>
                <span className="view-detail-value">
                  {currentUserProfile?.username || "—"}
                </span>
              </div>
              <div className="view-detail-item">
                <span className="view-detail-label">Email Address</span>
                <span className="view-detail-value">
                  {currentUserProfile?.emailId || "—"}
                </span>
              </div>
              <div className="view-detail-item">
                <span className="view-detail-label">Phone Number</span>
                <span className="view-detail-value">
                  {currentUserProfile?.phonenumber ||
                    currentUserProfile?.mobileNumber ||
                    "—"}
                </span>
              </div>
              <div className="view-detail-item">
                <span className="view-detail-label">User Role</span>
                <span className="view-detail-value">
                  {currentUserProfile?.role || userRole}
                </span>
              </div>
              <div className="view-detail-item">
                <span className="view-detail-label">Account Status</span>
                <span
                  className="view-detail-value"
                  style={{ color: "#059669", display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: "#10b981",
                      display: "inline-block",
                    }}
                  ></span>
                  Active
                </span>
              </div>
              <div className="view-detail-item">
                <span className="view-detail-label">Member Since</span>
                <span className="view-detail-value">
                  {currentUserProfile?.createdAt
                    ? new Date(currentUserProfile.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "Active Member"}
                </span>
              </div>
            </div>

            <div style={{ marginTop: "24px", display: "flex", gap: "12px" }}>
              <button
                type="button"
                className="user-btn-primary"
                style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
                onClick={() => handleOpenEdit(currentUserProfile)}
              >
                <MdEdit size={16} />
                Edit Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== USER MANAGEMENT VIEW (Admin only) ===================== */}
      {showUserManagement && (
        <>
          {/* Summary Stat Cards */}
          <div className="user-stats-container">
            <div className="user-stat-card">
              <div className="stat-icon total">
                <MdPeopleAlt />
              </div>
              <div className="stat-info">
                <span className="stat-label">Total Users</span>
                <span className="stat-value">{stats.total}</span>
              </div>
            </div>
            <div className="user-stat-card">
              <div className="stat-icon superadmin">
                <MdAdminPanelSettings />
              </div>
              <div className="stat-info">
                <span className="stat-label">Super Admins</span>
                <span className="stat-value">{stats.superAdminCount}</span>
              </div>
            </div>
            <div className="user-stat-card">
              <div className="stat-icon admin">
                <MdAdminPanelSettings />
              </div>
              <div className="stat-info">
                <span className="stat-label">Administrators</span>
                <span className="stat-value">{stats.adminCount}</span>
              </div>
            </div>
            <div className="user-stat-card">
              <div className="stat-icon marketing">
                <MdCampaign />
              </div>
              <div className="stat-info">
                <span className="stat-label">Marketing</span>
                <span className="stat-value">{stats.marketingCount}</span>
              </div>
            </div>
            <div className="user-stat-card">
              <div className="stat-icon firmware">
                <MdMemory />
              </div>
              <div className="stat-info">
                <span className="stat-label">Firmware Specialists</span>
                <span className="stat-value">{stats.firmwareCount}</span>
              </div>
            </div>
          </div>

          {/* Filters & Search Bar */}
          <div className="user-filter-bar">
            <div className="filter-left">
              <div className="filter-search-box">
                <MdSearch className="search-icon-inside" />
                <input
                  type="text"
                  placeholder="Search by name, email, phone or role..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <select
                className="filter-select"
                value={selectedRole}
                onChange={(e) => {
                  setSelectedRole(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="ALL">All Roles</option>
                <option value="Super Admin">Super Admin</option>
                <option value="Administrator">Administrator</option>
                <option value="Firmware Specialist">Firmware Specialist</option>
                <option value="Marketing">Marketing</option>
              </select>
              {(searchQuery || selectedRole !== "ALL") && (
                <button className="btn-clear-filter" onClick={clearFilters}>
                  Clear Filter
                </button>
              )}
            </div>
            <div className="filter-right">
              <span className="filter-count">
                Showing <strong>{filteredUsers.length}</strong> of{" "}
                <strong>{users.length}</strong> users
              </span>
              <button className="btn-refresh" onClick={loadData}>
                <MdRefresh size={16} /> Refresh
              </button>
            </div>
          </div>

          {/* Users List Table */}
          <div className="user-table-wrapper">
            <table className="user-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: "60px", textAlign: "center" }}>SL NO.</th>
                  <th>USER</th>
                  <th>PHONE NUMBER</th>
                  <th>USER ROLE</th>
                  <th>MEMBER SINCE</th>
                  <th style={{ textAlign: "center", width: "160px" }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "40px" }}>
                      <p style={{ color: "#666", fontSize: "15px" }}>Loading users...</p>
                    </td>
                  </tr>
                ) : paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="table-empty-state">
                        <MdPeopleAlt />
                        <h4>No users found</h4>
                        <p>Try adjusting your search criteria.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user, idx) => {
                    const slNo = (currentPage - 1) * itemsPerPage + idx + 1;
                    const phone = user.phonenumber || user.mobileNumber || "—";
                    const roleClass = (user.role || "user")
                      .toLowerCase()
                      .replace(/\s+/g, "-");
                    const initial = (user.username || "U")[0]?.toUpperCase() || "U";
                    const memberDate = user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—";

                    return (
                      <tr key={user.emailId || idx}>
                        <td style={{ textAlign: "center", fontWeight: "600", color: "#6b7280" }}>
                          {slNo}
                        </td>
                        <td>
                          <div className="user-avatar-cell">
                            <div className="user-avatar-circle">{initial}</div>
                            <div>
                              <div className="user-name-text">{user.username}</div>
                              <div className="user-email-sub">{user.emailId}</div>
                            </div>
                          </div>
                        </td>
                        <td>{phone}</td>
                        <td>
                          <span className={`role-badge ${roleClass}`}>
                            {user.role || "Administrator"}
                          </span>
                        </td>
                        <td>{memberDate}</td>
                        <td style={{ textAlign: "center" }}>
                          <div className="action-buttons-group" style={{ justifyContent: "center" }}>
                            <button
                              className="action-btn view"
                              onClick={() => handleOpenView(user)}
                              title="View user details"
                            >
                              <MdVisibility size={14} />
                              VIEW
                            </button>
                            <button
                              className="action-btn edit"
                              onClick={() => handleOpenEdit(user)}
                              title="Edit user"
                            >
                              <MdEdit size={14} />
                              EDIT
                            </button>
                            {canDeleteUser && (
                              <button
                                className="action-btn delete"
                                onClick={() => handleOpenDelete(user)}
                                title={
                                  (currentUserProfile?.emailId || localStorage.getItem("userEmail") || "").toLowerCase() === (user.emailId || "").toLowerCase()
                                    ? "Cannot delete your own account"
                                    : ["admin@studds.com", "admin@gmail.com", "superadmin@studds.com", "super admin", "it.alert@studds.com"].includes((user.emailId || "").toLowerCase()) ||
                                      ["admin@studds.com", "admin@gmail.com", "superadmin@studds.com", "super admin", "it.alert@studds.com"].includes((user.username || "").toLowerCase())
                                    ? "Protected root administrator / Super Admin account"
                                    : "Delete user"
                                }
                                disabled={
                                  (currentUserProfile?.emailId || localStorage.getItem("userEmail") || "").toLowerCase() === (user.emailId || "").toLowerCase() ||
                                  ["admin@studds.com", "admin@gmail.com", "superadmin@studds.com", "super admin", "it.alert@studds.com"].includes((user.emailId || "").toLowerCase()) ||
                                  ["admin@studds.com", "admin@gmail.com", "superadmin@studds.com", "super admin", "it.alert@studds.com"].includes((user.username || "").toLowerCase())
                                }
                              >
                                <MdDeleteOutline size={14} />
                                DELETE
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Pagination Footer */}
            {filteredUsers.length > itemsPerPage && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Page {currentPage} of {totalPages} ({filteredUsers.length} total users)
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  >
                    Previous
                  </button>
                  <span className="pagination-page-indicator">{currentPage}</span>
                  <button
                    className="pagination-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modals */}
      {canManageUsers && (
        <AddUserModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={handleAddUserSuccess}
          onError={(err) => showSnackbar(err, "error")}
        />
      )}

      <ViewUserModal
        isOpen={isViewModalOpen}
        user={selectedUser}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedUser(null);
        }}
        canDelete={canDeleteUser}
        onDelete={handleOpenDelete}
      />

      <EditUserModal
        isOpen={isEditModalOpen}
        user={selectedUser}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleEditUserSuccess}
        onError={(err) => showSnackbar(err, "error")}
      />

      {/* Delete Confirmation Modal */}
      {deleteCandidate && (
        <div
          className="user-modal-overlay"
          onClick={() => !isDeleting && setDeleteCandidate(null)}
          style={{
            position: "fixed",
            inset: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(3px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            padding: "20px",
            boxSizing: "border-box",
          }}
        >
          <div
            className="user-modal-card delete-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: "460px",
              boxShadow: "0 25px 60px rgba(0, 0, 0, 0.35)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "#fee2e2",
                borderBottom: "1px solid #fecaca",
                padding: "20px 24px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                <MdDeleteOutline size={26} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#991b1b" }}>
                  Confirm User Deletion
                </h3>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#b91c1c" }}>
                  This action is permanent and cannot be undone.
                </p>
              </div>
            </div>

            <div style={{ padding: "24px", color: "#374151" }}>
              <p style={{ margin: "0 0 16px 0", fontSize: "14px", lineHeight: "1.5" }}>
                Are you sure you want to permanently delete user{" "}
                <strong style={{ color: "#111827" }}>{deleteCandidate.username || deleteCandidate.emailId}</strong>?
              </p>

              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  fontSize: "13px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <div>
                  <span style={{ color: "#6b7280" }}>Email: </span>
                  <strong style={{ color: "#111827" }}>{deleteCandidate.emailId}</strong>
                </div>
                <div>
                  <span style={{ color: "#6b7280" }}>Role: </span>
                  <span
                    className={`role-badge ${(deleteCandidate.role || "user")
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    style={{ display: "inline-block", fontSize: "11px", padding: "2px 8px" }}
                  >
                    {deleteCandidate.role || "Administrator"}
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                padding: "16px 24px",
                background: "#f9fafb",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setDeleteCandidate(null)}
                style={{
                  padding: "9px 18px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  background: "#ffffff",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: "13px",
                  cursor: isDeleting ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={confirmDeleteUser}
                style={{
                  padding: "9px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: isDeleting ? "#fca5a5" : "#dc2626",
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "13px",
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  boxShadow: "0 2px 6px rgba(220, 38, 38, 0.3)",
                }}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ShowSnacker
        open={snack.open}
        message={snack.message}
        type={snack.type}
        onClose={() => setSnack((s: any) => ({ ...s, open: false }))}
      />
    </div>
  );
}

export default DashBoardProfile;
